import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  DollarSign,
  Target,
  Activity,
  Calendar,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Minus,
  Info,
  HelpCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const SubsidiaryPerformanceModal = ({ 
  open, 
  onClose, 
  subsidiary,
  subsidiaries = [],
  teamMembers = [],
  userRoles = []
}) => {
  const [loading, setLoading] = useState(false);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (open && subsidiary) {
      fetchPerformanceData();
    }
  }, [open, subsidiary]);

  const fetchPerformanceData = async () => {
    if (!subsidiary) return;
    
    setLoading(true);
    try {
      // Fetch regular business plans (same as Navigation uses)
      const plansResponse = await axios.get(`${BACKEND_URL}/api/business-plans`);
      const allPlans = plansResponse.data || [];
      
      // Match plans by company name
      const subsidiaryPlans = allPlans.filter(plan => 
        plan.company_name?.toLowerCase().includes(subsidiary.name.toLowerCase()) ||
        plan.name?.toLowerCase().includes(subsidiary.name.toLowerCase())
      );
      
      setBusinessPlans(subsidiaryPlans);

      // Fetch financial data for these plans to calculate real metrics
      await calculateMetrics(subsidiaryPlans);
    } catch (error) {
      console.error("Error fetching performance data:", error);
      setBusinessPlans([]);
      calculateMetrics([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = async (plans) => {
    // Calculate team metrics
    const subsidiaryTeam = teamMembers.filter(member => {
      const memberRoles = userRoles.filter(ur => ur.user_id === member.id);
      return memberRoles.some(role => role.subsidiary_id === subsidiary.id);
    });

    const activeTeam = subsidiaryTeam.filter(m => m.is_active !== false);
    
    // Calculate child subsidiaries
    const childSubs = subsidiaries.filter(s => s.parent_id === subsidiary.id);
    const activeChildSubs = childSubs.filter(s => s.is_active !== false);

    // Fetch real financial data from business plans
    let totalRevenue = 0;
    let totalExpenses = 0;
    let revenueGrowth = 0;
    let profitMargin = 0;

    try {
      // Fetch revenue data for all plans
      for (const plan of plans) {
        try {
          const revenueResponse = await axios.get(`${BACKEND_URL}/api/revenue/${plan.id}`);
          const revenues = revenueResponse.data || [];
          
          // Sum up total revenue
          const planRevenue = revenues.reduce((sum, rev) => {
            return sum + (parseFloat(rev.total_revenue) || 0);
          }, 0);
          totalRevenue += planRevenue;

          // Fetch expense data
          const expenseResponse = await axios.get(`${BACKEND_URL}/api/expenses/${plan.id}`);
          const expenses = expenseResponse.data || [];
          
          const planExpenses = expenses.reduce((sum, exp) => {
            return sum + (parseFloat(exp.total_cost) || 0);
          }, 0);
          totalExpenses += planExpenses;

          // Calculate growth rate from revenue trends
          if (revenues.length >= 2) {
            const sortedRevenues = [...revenues].sort((a, b) => a.month - b.month);
            const firstMonth = parseFloat(sortedRevenues[0]?.total_revenue) || 0;
            const lastMonth = parseFloat(sortedRevenues[sortedRevenues.length - 1]?.total_revenue) || 0;
            
            if (firstMonth > 0) {
              const growth = ((lastMonth - firstMonth) / firstMonth) * 100;
              revenueGrowth = growth;
            }
          }
        } catch (err) {
          console.error(`Error fetching data for plan ${plan.id}:`, err);
        }
      }

      // Calculate profit margin
      if (totalRevenue > 0) {
        profitMargin = ((totalRevenue - totalExpenses) / totalRevenue) * 100;
      }

      // Calculate performance score based on multiple factors
      let performanceScore = 50; // Base score
      
      // Factor 1: Profit margin (max 25 points)
      if (profitMargin > 0) {
        performanceScore += Math.min(profitMargin / 2, 25);
      }
      
      // Factor 2: Revenue growth (max 20 points)
      if (revenueGrowth > 0) {
        performanceScore += Math.min(revenueGrowth, 20);
      }
      
      // Factor 3: Team efficiency (max 5 points)
      const teamEfficiency = subsidiaryTeam.length > 0 ? (activeTeam.length / subsidiaryTeam.length) * 5 : 0;
      performanceScore += teamEfficiency;

      // Cap at 100
      performanceScore = Math.min(Math.round(performanceScore), 100);

      // Calculate utilization rate based on active team percentage
      const utilizationRate = subsidiaryTeam.length > 0 
        ? Math.round((activeTeam.length / subsidiaryTeam.length) * 100)
        : 0;

      setMetrics({
        team: {
          total: subsidiaryTeam.length,
          active: activeTeam.length,
          inactive: subsidiaryTeam.length - activeTeam.length
        },
        subsidiaries: {
          total: childSubs.length,
          active: activeChildSubs.length
        },
        plans: {
          total: plans.length,
          active: plans.length
        },
        performance: {
          score: performanceScore || 50, // Fallback to 50 if no data
          growth: parseFloat(revenueGrowth.toFixed(1)) || 0,
          utilization: utilizationRate || 0
        },
        financial: {
          totalRevenue: totalRevenue,
          totalExpenses: totalExpenses,
          profitMargin: parseFloat(profitMargin.toFixed(1))
        }
      });
    } catch (error) {
      console.error("Error calculating metrics:", error);
      
      // Fallback metrics if API calls fail
      setMetrics({
        team: {
          total: subsidiaryTeam.length,
          active: activeTeam.length,
          inactive: subsidiaryTeam.length - activeTeam.length
        },
        subsidiaries: {
          total: childSubs.length,
          active: activeChildSubs.length
        },
        plans: {
          total: plans.length,
          active: plans.length
        },
        performance: {
          score: 50,
          growth: 0,
          utilization: subsidiaryTeam.length > 0 ? Math.round((activeTeam.length / subsidiaryTeam.length) * 100) : 0
        },
        financial: {
          totalRevenue: 0,
          totalExpenses: 0,
          profitMargin: 0
        }
      });
    }
  };

  if (!subsidiary) return null;

  const getPerformanceColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-50";
    if (score >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowUp className="h-4 w-4" />;
    if (growth < 0) return <ArrowDown className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <div className="text-xl font-bold">{subsidiary.name} - Performance Dashboard</div>
              <div className="text-sm text-gray-500 font-normal mt-1">
                {subsidiary.location && `${subsidiary.location} • `}
                {subsidiary.subsidiary_type || "Subsidiary"}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-gray-500">
            <Activity className="h-8 w-8 animate-spin mx-auto mb-2" />
            Loading performance data...
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Key Performance Indicators */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Target className="h-4 w-4" />
                KEY PERFORMANCE INDICATORS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Performance Score */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-500">Performance Score</p>
                          <div className="group relative">
                            <HelpCircle className="h-3 w-3 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <strong>Calculation:</strong><br/>
                              Base (50) + Profit Margin (max 25) + Revenue Growth (max 20) + Team Efficiency (max 5)<br/><br/>
                              <strong>Higher is better:</strong> 80+ Excellent, 60-79 Good, &lt;60 Needs Work
                            </div>
                          </div>
                        </div>
                        <p className={`text-3xl font-bold ${getPerformanceColor(metrics?.performance.score || 0)}`}>
                          {metrics?.performance.score || 0}%
                        </p>
                      </div>
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center ${getPerformanceColor(metrics?.performance.score || 0)}`}>
                        <Target className="h-7 w-7" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Growth Rate */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-500">Growth Rate</p>
                          <div className="group relative">
                            <HelpCircle className="h-3 w-3 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <strong>Calculation:</strong><br/>
                              ((Latest Month Revenue - First Month Revenue) / First Month Revenue) × 100<br/><br/>
                              <strong>Positive</strong> = Revenue increasing<br/>
                              <strong>Negative</strong> = Revenue declining
                            </div>
                          </div>
                        </div>
                        <p className={`text-3xl font-bold ${metrics?.performance.growth > 0 ? 'text-green-600' : metrics?.performance.growth < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {metrics?.performance.growth > 0 ? '+' : ''}{metrics?.performance.growth || 0}%
                        </p>
                      </div>
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center ${metrics?.performance.growth > 0 ? 'bg-green-50 text-green-600' : metrics?.performance.growth < 0 ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-600'}`}>
                        {metrics?.performance.growth > 0 ? <TrendingUp className="h-7 w-7" /> : <TrendingDown className="h-7 w-7" />}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Utilization Rate */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-500">Utilization Rate</p>
                          <div className="group relative">
                            <HelpCircle className="h-3 w-3 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <strong>Calculation:</strong><br/>
                              (Active Team Members / Total Team Members) × 100<br/><br/>
                              <strong>Measures:</strong> Workforce efficiency and active participation rate
                            </div>
                          </div>
                        </div>
                        <p className="text-3xl font-bold text-blue-600">
                          {metrics?.performance.utilization || 0}%
                        </p>
                      </div>
                      <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                        <Activity className="h-7 w-7" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Operational Metrics */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                OPERATIONAL METRICS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Team Members */}
                <Card className="bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-8 w-8 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-blue-900">{metrics?.team.total || 0}</p>
                        <p className="text-xs text-blue-700">Team Members</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs bg-white">
                            {metrics?.team.active || 0} Active
                          </Badge>
                          {(metrics?.team.inactive || 0) > 0 && (
                            <Badge variant="outline" className="text-xs bg-white">
                              {metrics?.team.inactive} Inactive
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Business Plans */}
                <Card className="bg-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Target className="h-8 w-8 text-purple-600" />
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-purple-900">{metrics?.plans.total || 0}</p>
                        <p className="text-xs text-purple-700">Business Plans</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs bg-white">
                            {metrics?.plans.active || 0} Active
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Child Subsidiaries */}
                <Card className="bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="h-8 w-8 text-green-600" />
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-green-900">{metrics?.subsidiaries.total || 0}</p>
                        <p className="text-xs text-green-700">Child Subsidiaries</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs bg-white">
                            {metrics?.subsidiaries.active || 0} Active
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Financial Metrics */}
            {metrics?.financial && (metrics.financial.totalRevenue > 0 || metrics.financial.totalExpenses > 0) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  FINANCIAL METRICS
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Revenue */}
                  <Card className="bg-green-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-8 w-8 text-green-600" />
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-green-900">
                            ${(metrics.financial.totalRevenue / 1000).toFixed(1)}K
                          </p>
                          <p className="text-xs text-green-700">Total Revenue</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs bg-white">
                              From {businessPlans.length} plan{businessPlans.length > 1 ? 's' : ''}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Total Expenses */}
                  <Card className="bg-red-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingDown className="h-8 w-8 text-red-600" />
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-red-900">
                            ${(metrics.financial.totalExpenses / 1000).toFixed(1)}K
                          </p>
                          <p className="text-xs text-red-700">Total Expenses</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs bg-white">
                              Operating Costs
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Profit Margin */}
                  <Card className={`${metrics.financial.profitMargin > 0 ? 'bg-blue-50' : 'bg-yellow-50'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className={`h-8 w-8 ${metrics.financial.profitMargin > 0 ? 'text-blue-600' : 'text-yellow-600'}`} />
                        <div className="flex-1">
                          <p className={`text-2xl font-bold ${metrics.financial.profitMargin > 0 ? 'text-blue-900' : 'text-yellow-900'}`}>
                            {metrics.financial.profitMargin > 0 ? '+' : ''}{metrics.financial.profitMargin}%
                          </p>
                          <p className={`text-xs ${metrics.financial.profitMargin > 0 ? 'text-blue-700' : 'text-yellow-700'}`}>Profit Margin</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-xs bg-white">
                              {metrics.financial.profitMargin > 20 ? 'Excellent' : metrics.financial.profitMargin > 10 ? 'Good' : 'Needs Improvement'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Business Plans List */}
            {businessPlans.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Business Plans ({businessPlans.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {businessPlans.map((plan) => (
                      <div key={plan.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Target className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{plan.name}</p>
                            <p className="text-xs text-gray-500">
                              {plan.company_name} • {plan.currency}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            Created {new Date(plan.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performance Insights */}
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <CardContent className="p-4">
                <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Performance Insights
                </h4>
                <div className="space-y-2 text-sm">
                  {metrics?.performance.score >= 80 && (
                    <p className="text-green-700">
                      ✓ Excellent performance! This subsidiary is operating at optimal levels with a {metrics.performance.score}% performance score.
                    </p>
                  )}
                  {metrics?.performance.score >= 60 && metrics?.performance.score < 80 && (
                    <p className="text-yellow-700">
                      • Good performance at {metrics.performance.score}%. Focus on improving profit margins and team efficiency for better results.
                    </p>
                  )}
                  {metrics?.performance.score < 60 && (
                    <p className="text-red-700">
                      ⚠ Performance needs attention ({metrics.performance.score}%). Consider reviewing revenue streams and cost management.
                    </p>
                  )}
                  {metrics?.performance.growth > 5 && (
                    <p className="text-green-700">
                      ✓ Strong growth trajectory with {metrics.performance.growth}% revenue growth rate.
                    </p>
                  )}
                  {metrics?.performance.growth < 0 && (
                    <p className="text-red-700">
                      ⚠ Revenue declining at {metrics.performance.growth}%. Strategic intervention recommended.
                    </p>
                  )}
                  {metrics?.financial && metrics.financial.profitMargin > 20 && (
                    <p className="text-green-700">
                      ✓ Excellent profit margin of {metrics.financial.profitMargin}% indicates strong financial health.
                    </p>
                  )}
                  {metrics?.financial && metrics.financial.profitMargin < 0 && (
                    <p className="text-red-700">
                      ⚠ Operating at a loss ({metrics.financial.profitMargin}% margin). Review expenses and revenue strategies.
                    </p>
                  )}
                  {metrics?.team.total > 0 && (
                    <p className="text-blue-700">
                      • Team size of {metrics.team.total} members with {((metrics.team.active / metrics.team.total) * 100).toFixed(0)}% active workforce.
                    </p>
                  )}
                  {businessPlans.length > 0 && (
                    <p className="text-purple-700">
                      • {businessPlans.length} active business plan{businessPlans.length > 1 ? 's' : ''} with real financial data integrated.
                    </p>
                  )}
                  {metrics?.subsidiaries.total > 0 && (
                    <p className="text-gray-700">
                      • Managing {metrics.subsidiaries.total} child subsidiaries with {metrics.subsidiaries.active} currently active.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubsidiaryPerformanceModal;
